import { MessageSquareText } from 'lucide-react';
import { DiscardChangesDialog } from '@/components/common/DiscardChangesDialog';
import {
  FormDialog,
  FormSection,
  FormStatusField,
  FormTextareaField,
  FormTextField,
} from '@/components/common/FormDialog';
import { useFeedbackTypeForm } from '../hooks/useFeedbackTypeForm';
import type { FeedbackType } from '../types/feedbackType.types';

const DESCRIPTION_MAX = 500;

interface FeedbackTypeFormModalProps {
  feedbackType: FeedbackType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackTypeFormModal({
  feedbackType,
  open,
  onOpenChange,
}: FeedbackTypeFormModalProps) {
  const {
    form,
    isEditMode,
    isPending,
    submit,
    isDiscardDialogOpen,
    requestClose,
    confirmDiscard,
    cancelDiscard,
  } = useFeedbackTypeForm({
    feedbackType,
    open,
    onClose: () => onOpenChange(false),
  });

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      requestClose();
      return;
    }
    onOpenChange(nextOpen);
  }

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={handleOpenChange}
        icon={MessageSquareText}
        title={isEditMode ? 'Edit Feedback Type' : 'Add Feedback Type'}
        description="A feedback type is what the field picks when they send a feedback."
        form={form}
        isPending={isPending}
        isEditMode={isEditMode}
        onSubmit={submit}
      >
        <FormSection>
          <FormTextField control={form.control} name="code" label="Code" hasTabularNumbers />
          <FormTextField control={form.control} name="name" label="Name" />
          <FormTextareaField
            control={form.control}
            name="description"
            label="Description"
            maxLength={DESCRIPTION_MAX}
            isFullRow
          />
        </FormSection>
        <FormStatusField control={form.control} name="isActive" />
      </FormDialog>

      <DiscardChangesDialog
        open={isDiscardDialogOpen}
        onOpenChange={(nextOpen) => !nextOpen && cancelDiscard()}
        onDiscard={confirmDiscard}
      />
    </>
  );
}
